import { CheckCircleOutlined, CloseCircleOutlined, FieldTimeOutlined, MailOutlined, PhoneOutlined, ScheduleOutlined, UserOutlined } from "@ant-design/icons"
import { Alert, Button, Card, DatePicker, Form, Input, Spin, Select, Space, Descriptions, Image } from "antd"
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { createCode, getBusinessClients } from "../../store/slices/codeSlice";
import { clearUsersList, getUsersList } from "../../store/slices/userSlice";
import { clearJobsByUser, formattedDateTime, getJobById, getJobsByUser, invoiceSentToCustomer } from "../../store/slices/jobSlice";

const AddInvoice = () => {
    const [form] = Form.useForm();
    const [btnLoading, setBtnLoading] = useState(false);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [alertMessage, setAlertMessage] = useState("");
    const [alertType, setAlertType] = useState("success");
    const [address, setAddress] = useState("")
    const { usersList, loading } = useSelector((state) => state?.user)
    const { jobsByUser, jobData } = useSelector((state) => state?.jobs)
    useEffect(() => {
        dispatch(getBusinessClients())
    }, [dispatch])

    useEffect(() => {
        if (jobsByUser?.length) {
            console.log("Jobs fetched:", jobsByUser);
        }
    }, [jobsByUser]);

    useEffect(() => {
        if (typeof jobData?.delivery_address == "object") {
            setAddress(`${jobData?.delivery_address?.address}, ${jobData?.delivery_address?.city}`)
        } else {
            setAddress(`${jobData?.delivery_address}`)
        }
    }, [jobData])

    const handleSearch = (value) => {
        if (value.length == 0) {
            dispatch(clearUsersList())
        }
        if (value.length < 2) return;
        dispatch(getUsersList({ roleType: "user", search: value }));
    };

    const onClear = () => {
        dispatch(clearUsersList());
    }

    const handleUserChange = (id) => {
        dispatch(clearJobsByUser())
        form.setFieldsValue({job_id: null})
        if (!id) {
            return
        }
        dispatch(getJobsByUser({id}))
    }

    const handleJobChange = (id) => {
        if (!id) {
            dispatch(clearJobsByUser())
            return
        }
        dispatch(getJobById(id))
    }

    const onSubmit = async (values) => {
        setBtnLoading(true);
        setAlertMessage("");
        try {
            await new Promise(resolve => setTimeout(resolve, 1000));
            const resultAction = await dispatch(invoiceSentToCustomer(values))
            if (invoiceSentToCustomer.fulfilled.match(resultAction)) {
                setAlertMessage(resultAction?.payload?.message || 'Invoice sent successfully');
                setAlertType('success');
                form.resetFields();
                dispatch(clearJobsByUser())
                setTimeout(() => {
                    setAlertMessage('')
                }, 4000)
            } else {
                console.log(resultAction?.payload)
                setAlertMessage(resultAction?.payload?.error || 'Error sending invoice');
                setAlertType('error');
            }
        } catch (error) {
            setAlertMessage('Error sending invoice');
            setAlertType('error');
        } finally {
            setBtnLoading(false);
        }
    }
    return (
        <div>
            <h1 style={{ marginBottom: 24 }}>Invoice</h1>
            <Card title="Create Invoice" style={{ marginBottom: 16 }}>
                {alertMessage && (
                    <Alert
                        message={alertMessage}
                        type={alertType}
                        showIcon
                        icon={alertType === 'success' ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                        closable
                        onClose={() => setAlertMessage('')}
                        style={{
                            marginBottom: 24,
                            borderRadius: '8px',
                        }}
                    />
                )}
                <Form
                    layout="vertical"
                    onFinish={onSubmit}
                    form={form}
                >
                    <Form.Item
                        label="User"
                        name="user_id"
                        rules={[{ required: true, message: 'Please enter user ID' }]}
                    >
                        <Select
                        showSearch
                        allowClear
                        placeholder="Type at least 2 characters to search"
                        filterOption={false} // disable local filtering
                        onSearch={handleSearch} // called when user types
                        notFoundContent={loading ? <Spin size="small" /> : null}
                        onClear={onClear}
                        options={usersList?.data?.map((data) => ({
                            value: data._id,
                            label: `${data.first_name} ${data?.last_name}`,
                        }))}
                        onChange={(id) => handleUserChange(id)}
                        />
                    </Form.Item>
                    <Form.Item
                        label="Job"
                        name="job_id"
                        rules={[{ required: true, message: "Select job" }]}
                    >
                        <Select
                        placeholder="Select job"
                        options={(jobsByUser || []).map((sub) => ({
                            key : sub._id,
                            value: sub._id,
                            label: sub._id,
                        }))}
                        onChange={(id) => handleJobChange(id)}
                        disabled={!jobsByUser?.length}
                        />
                    </Form.Item>
                    <Form.Item>
                        <Space>
                            <Button
                            type="primary"
                            htmlType="submit"
                            loading={btnLoading}
                            size="large"
                            block
                            style={{
                                height: '48px',
                                fontSize: '16px',
                                fontWeight: 600,
                                borderRadius: '8px',
                            }}
                            >
                                Save Changes
                            </Button>
                            <Button onClick={(e) => navigate(-1)}>Back</Button>
                        </Space>
                    </Form.Item>
                </Form>
            </Card>

            {jobData ? (
                <>
                    <Card
                        title={
                            <span style={{ fontSize: '18px', fontWeight: 600 }}>
                                <UserOutlined style={{ marginRight: 8, color: '#CA9AE8' }} />
                                User Information
                            </span>
                        }
                        style={{
                            marginBottom: 24,
                            borderRadius: '12px',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                            border: 'none',
                        }}
                    >
                        <Descriptions column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }} bordered>
                            <Descriptions.Item label={<span><UserOutlined style={{ marginRight: 4 }} />Name</span>}>
                                {`${jobData?.user_id?.first_name} ${jobData?.user_id?.last_name}`}
                            </Descriptions.Item>
                            <Descriptions.Item label={<span><MailOutlined style={{ marginRight: 4 }} />Email</span>}>
                                {jobData?.user_id?.email || 'N/A'}
                            </Descriptions.Item>
                            <Descriptions.Item label={<span><UserOutlined style={{ marginRight: 4 }} />Gender</span>}>
                                {jobData?.user_id?.gender || 'N/A'}
                            </Descriptions.Item>
                            <Descriptions.Item label={<span><PhoneOutlined style={{ marginRight: 4 }} />Phone Number</span>}>
                                {jobData?.user_id?.phone || 'N/A'}
                            </Descriptions.Item>
                        </Descriptions>
                    </Card>
                    <Card
                        title={
                            <span style={{ fontSize: '18px', fontWeight: 600 }}>
                                <UserOutlined style={{ marginRight: 8, color: '#CA9AE8' }} />
                                Job Information
                            </span>
                        }
                        style={{
                            marginBottom: 24,
                            borderRadius: '12px',
                            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
                            border: 'none',
                        }}
                    >
                        <Descriptions column={{ xxl: 2, xl: 2, lg: 2, md: 1, sm: 1, xs: 1 }} bordered>
                            <Descriptions.Item label={<span><ScheduleOutlined style={{ marginRight: 4 }} />Job Date</span>}>
                                {`${new Date(jobData?.job_date).toLocaleDateString('en-CA')}`}
                            </Descriptions.Item>
                            <Descriptions.Item label={<span><MailOutlined style={{ marginRight: 4 }} />Shift</span>}>
                                {jobData?.time_slot || 'N/A'}
                            </Descriptions.Item>
                            <Descriptions.Item label={<span><FieldTimeOutlined style={{ marginRight: 4 }} />Job Start Time</span>}>
                                {jobData?.job_start_time ? formattedDateTime(new Date(jobData?.job_start_time)) : 'N/A'}
                            </Descriptions.Item>
                            <Descriptions.Item label={<span><FieldTimeOutlined style={{ marginRight: 4 }} />Job End Time</span>}>
                                {jobData?.job_end_time ? formattedDateTime(new Date(jobData?.job_end_time)) : 'N/A'}
                            </Descriptions.Item>
                            <Descriptions.Item label={<span><FieldTimeOutlined style={{ marginRight: 4 }} />Status</span>}>
                                {jobData?.status || 'N/A'}
                            </Descriptions.Item>
                            <Descriptions.Item label={<span><FieldTimeOutlined style={{ marginRight: 4 }} />Delivery Address</span>}>
                                {address || 'N/A'}
                            </Descriptions.Item>
                        </Descriptions>
                        <Image.PreviewGroup>
                            {jobData?.images?.fileList?.map((img, idx) => (
                                <Image
                                key={idx}
                                width={150}
                                src={img?.thumbUrl}
                                style={{ marginRight: 8, marginBottom: 8 }}
                                />
                            ))}
                            </Image.PreviewGroup>
                    </Card>
                </>
            ): ""}
        </div>
    )
}

export default AddInvoice